// Allow-list HTML sanitiser for content we did not write (bot answers, visitor messages, file names).
// Same rules as public/widget.js: formatting tags only, no event handlers, only http(s)/mailto/tel links
// and http(s)/blob/base64 raster images. Keep both in sync.

const SAFE_TAGS = new Set(['A', 'ABBR', 'B', 'BLOCKQUOTE', 'BR', 'CAPTION', 'CODE', 'DEL', 'DIV', 'EM', 'H1', 'H2', 'H3',
  'H4', 'H5', 'H6', 'HR', 'I', 'IMG', 'INS', 'KBD', 'LI', 'MARK', 'OL', 'P', 'PRE', 'S', 'SMALL', 'SPAN', 'STRONG',
  'SUB', 'SUP', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'U', 'UL']);
// removed together with their content; any other unknown tag is unwrapped (its text is kept)
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'FRAME', 'FRAMESET', 'OBJECT', 'EMBED', 'APPLET', 'SVG', 'MATH',
  'TEMPLATE', 'NOSCRIPT', 'NOEMBED', 'NOFRAMES', 'XMP', 'PLAINTEXT', 'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT',
  'OPTION', 'LINK', 'META', 'BASE', 'TITLE', 'HEAD', 'AUDIO', 'VIDEO', 'SOURCE', 'TRACK', 'CANVAS', 'PORTAL']);
const SAFE_ATTRS = new Set(['class', 'style', 'title', 'alt', 'href', 'src', 'target', 'rel', 'colspan', 'rowspan',
  'start', 'align', 'width', 'height', 'download']);
const XHTML_NS = 'http://www.w3.org/1999/xhtml';

// Returns the URL if its scheme is safe for the given use ('href' or 'img'), else ''.
export function safeUrl(url, kind) {
  const raw = String(url ?? '').trim();
  if (!raw) return '';
  // browsers ignore control characters and spaces inside a scheme ("java\nscript:")
  // eslint-disable-next-line no-control-regex
  const compact = raw.replace(/[\u0000- \u007f-\u009f]/g, '');
  if (/^data:/i.test(compact)) {
    return kind === 'img' && /^data:image\/(png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i.test(compact) ? raw : '';
  }
  const scheme = compact.match(/^([a-z][a-z0-9+.-]*):/i);
  if (!scheme) return raw; // relative URL
  const allowed = kind === 'img' ? ['http', 'https', 'blob'] : ['http', 'https', 'mailto', 'tel'];
  return allowed.includes(scheme[1].toLowerCase()) ? raw : '';
}

function cleanChildren(parent) {
  Array.from(parent.childNodes).forEach((node) => {
    if (node.nodeType === 3) return; // text
    if (node.nodeType !== 1) { parent.removeChild(node); return; } // comments, processing instructions
    const tag = node.nodeName.toUpperCase();
    if (!SAFE_TAGS.has(tag) || node.namespaceURI !== XHTML_NS) {
      if (DROP_TAGS.has(tag) || node.namespaceURI !== XHTML_NS) { parent.removeChild(node); return; }
      cleanChildren(node);
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
      return;
    }
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      let keep = SAFE_ATTRS.has(name);
      if (keep && name === 'href') keep = tag === 'A' && !!safeUrl(attr.value, 'href');
      if (keep && name === 'src') keep = tag === 'IMG' && !!safeUrl(attr.value, 'img');
      if (keep && name === 'style') keep = !/url\s*\(|expression|javascript:|@import|behavior|binding|position\s*:\s*(fixed|absolute|sticky)/i.test(attr.value);
      if (!keep) node.removeAttribute(attr.name);
    });
    if (tag === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
    cleanChildren(node);
  });
}

export function sanitizeHtml(html) {
  if (html === null || html === undefined || html === '') return '';
  const tpl = document.createElement('template'); // inert: nothing runs or loads while we clean it
  tpl.innerHTML = String(html);
  cleanChildren(tpl.content);
  return tpl.innerHTML;
}

export default sanitizeHtml;
